import { ReactNode, useState } from "react";
import { UseFormReturn, FieldValues, FormProvider } from "react-hook-form";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface FormModalProps<T extends FieldValues> {
    isOpen: boolean;
    onClose: () => void;
    title: ReactNode;
    description?: string;
    children: ReactNode;
    form?: UseFormReturn<T>;
    onSubmit?: (data: T) => void;
    isLoading?: boolean;
    submitLabel?: string;
    cancelLabel?: string;
    maxWidth?: string;
    customFooter?: ReactNode;
    // If true, the form wrapping is handled by the parent
    noFormWrapper?: boolean;
    // If true, shows a confirmation dialog when closing with unsaved changes
    confirmBeforeClose?: boolean;
}

const FormModal = <T extends FieldValues>({
    isOpen,
    onClose,
    title,
    description,
    children,
    form,
    onSubmit,
    isLoading = false,
    submitLabel = "Guardar",
    cancelLabel = "Cancelar",
    maxWidth = "max-w-md",
    customFooter,
    noFormWrapper = false,
    confirmBeforeClose = false,
}: FormModalProps<T>) => {
    const [showCloseConfirm, setShowCloseConfirm] = useState(false);

    const isDirty = confirmBeforeClose && form?.formState.isDirty;

    const attemptClose = () => {
        if (isDirty) {
            setShowCloseConfirm(true);
        } else {
            onClose();
        }
    };

    const confirmClose = () => {
        setShowCloseConfirm(false);
        onClose();
    };

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            attemptClose();
        }
    };

    const Content = (
        <div className="space-y-4">
            {children}

            {customFooter ? (
                customFooter
            ) : (
                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={attemptClose}
                        disabled={isLoading}
                    >
                        {cancelLabel}
                    </Button>
                    <Button
                        type="submit"
                        disabled={isLoading || (form ? !form.formState.isValid : false)}
                        className="bg-brand-primary hover:bg-brand-primary-hover"
                    >
                        {isLoading ? "Guardando..." : submitLabel}
                    </Button>
                </DialogFooter>
            )}
        </div>
    );

    return (
        <>
            <Dialog open={isOpen} onOpenChange={handleOpenChange}>
                <DialogContent className={cn(maxWidth, "max-h-[90vh] overflow-y-auto")} onPointerDownOutside={(e) => {
                    if (isDirty) {
                        e.preventDefault();
                        setShowCloseConfirm(true);
                    }
                }} onEscapeKeyDown={(e) => {
                    if (isDirty) {
                        e.preventDefault();
                        setShowCloseConfirm(true);
                    }
                }}>
                    <DialogHeader>
                        <DialogTitle>{title}</DialogTitle>
                        {description && <DialogDescription>{description}</DialogDescription>}
                    </DialogHeader>

                    {/* If form is provided and wrapper is not disabled, wrap content in FormProvider and HTML form */}
                    {!noFormWrapper && form && onSubmit ? (
                        <FormProvider {...form}>
                            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                                {children}
                                {customFooter ? (
                                    customFooter
                                ) : (
                                    <DialogFooter>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={attemptClose}
                                            disabled={isLoading}
                                        >
                                            {cancelLabel}
                                        </Button>
                                        <Button
                                            type="submit"
                                            disabled={isLoading || !form.formState.isValid}
                                            className="bg-brand-primary hover:bg-brand-primary-hover"
                                        >
                                            {isLoading ? "Guardando..." : submitLabel}
                                        </Button>
                                    </DialogFooter>
                                )}
                            </form>
                        </FormProvider>
                    ) : (
                        // Otherwise just render content (parent handles form tag or no form needed)
                        Content
                    )}
                </DialogContent>
            </Dialog>

            <AlertDialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>¿Salir sin guardar?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Tienes cambios sin guardar. Si sales, perderás los datos ingresados.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setShowCloseConfirm(false)}>
                            Continuar editando
                        </AlertDialogCancel>
                        <AlertDialogAction onClick={confirmClose}>
                            Salir sin guardar
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

export default FormModal;
